import dayjs from 'dayjs';
import type { RelationOption } from './relation-options';

export type CrmDateMode = 'date' | 'dateTime';
export const CRM_DATE_FORMAT = 'YYYY-MM-DD';
export const CRM_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const CRM_API_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

type RelationTextInput = {
  value?: unknown;
  directName?: unknown;
  fallbackName?: unknown;
  options?: RelationOption[];
  nameMap?: Record<string, string>;
  emptyText?: string;
};

const normalizeText = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

export const formatCrmDate = (
  value: unknown,
  mode: CrmDateMode = 'dateTime',
  emptyText = '-',
) => {
  if (!value) return emptyText;
  const parsed = dayjs(String(value));
  if (!parsed.isValid()) return emptyText;
  return parsed.format(
    mode === 'date' ? CRM_DATE_FORMAT : CRM_DATE_TIME_FORMAT,
  );
};

export const formatCrmDateTime = (value: unknown, emptyText = '-') =>
  formatCrmDate(value, 'dateTime', emptyText);

const toValidDayjs = (value: unknown) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }
  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  const parsed = dayjs(String(value));
  return parsed.isValid() ? parsed : null;
};

export const toApiDateTime = (value: unknown) => {
  const parsed = toValidDayjs(value);
  return parsed ? parsed.format(CRM_API_DATE_TIME_FORMAT) : undefined;
};

export const toApiDate = (value: unknown) => {
  const parsed = toValidDayjs(value);
  return parsed ? parsed.format(CRM_DATE_FORMAT) : undefined;
};

export const toApiDateRangeStart = (value: unknown) => {
  const parsed = toValidDayjs(value);
  return parsed
    ? parsed.startOf('day').format(CRM_API_DATE_TIME_FORMAT)
    : undefined;
};

export const toApiDateRangeEnd = (value: unknown) => {
  const parsed = toValidDayjs(value);
  return parsed
    ? parsed.endOf('day').format(CRM_API_DATE_TIME_FORMAT)
    : undefined;
};

export const resolveRelationText = ({
  value,
  directName,
  fallbackName,
  options = [],
  nameMap = {},
  emptyText = '-',
}: RelationTextInput) => {
  const direct = normalizeText(directName) || normalizeText(fallbackName);
  if (direct) return direct;

  const raw = normalizeText(value);
  if (!raw) return emptyText;

  const optionLabel = options.find((item) => item.value === raw)?.label;
  const normalizedOptionLabel = normalizeText(optionLabel);
  if (normalizedOptionLabel) return normalizedOptionLabel;

  const mappedLabel = normalizeText(nameMap[raw]);
  if (mappedLabel) return mappedLabel;

  return emptyText;
};

export const shouldHideTechnicalField = (field?: string) => {
  const normalized = normalizeText(field).toLowerCase();
  return (
    normalized === 'uuid' ||
    normalized === 'id' ||
    normalized === 'processinstanceuuid'
  );
};
